import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Skeleton,
} from '@barfacil/barfacil-ui'
import { ChevronDown, CirclePlus, Info, Settings } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { DeletePriceListDialog } from '@/app/panel/management/price-list/_components/delete-price-list-dialog'
import { FeedbackPriceListEmpty } from '@/app/panel/management/price-list/_components/feedback-empty'
import { PriceListProductsTable } from '@/app/panel/management/price-list/_components/price-list-products-table'
import { usePriceListContext } from '@/app/panel/management/price-list/_contexts/price-list-context'
import { Spinner } from '@/components/ui/spinner'

const PriceListItemLoading = () => (
  <div className="flex h-[50px] items-center justify-between rounded-md border border-gray-200 bg-gray-25 px-4">
    <Skeleton className="h-6 w-32 sm:w-56" />

    <ChevronDown size={16} />
  </div>
)

export const PriceListContent = () => {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const {
    priceListInfinite,
    priceList,
    hasPriceList,
    currentItem,
    setCurrentItem,
    clearSelectedValues,
    filters,
  } = usePriceListContext()

  function isCurrentItemSelected(id: number) {
    return currentItem === id.toString()
  }

  function handleNewProductFocus() {
    document.getElementById('price-list-new-product-combobox')?.click()
  }

  function handleOnValueChange(value: string) {
    clearSelectedValues()
    setCurrentItem(value)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && priceListInfinite.hasNextPage) {
          priceListInfinite.fetchNextPage()
        }
      },
      { threshold: 1 },
    )

    const sentinel = sentinelRef.current
    if (sentinel) observer.observe(sentinel)

    return () => {
      if (sentinel) observer.unobserve(sentinel)
    }
  }, [priceListInfinite, priceListInfinite.hasNextPage])

  if (priceListInfinite.isLoading) return <Spinner />

  if (!hasPriceList)
    return <FeedbackPriceListEmpty hasFilters={!!filters.search} />

  return (
    <Accordion
      className="flex flex-1 flex-col gap-4"
      type="single"
      collapsible
      value={currentItem}
      onValueChange={handleOnValueChange}
    >
      {priceList.map((item) => (
        <AccordionItem
          className="rounded-md border border-gray-200 bg-gray-25 px-4"
          key={item.cod_lista_preco}
          value={item.cod_lista_preco.toString()}
        >
          <div className="flex items-center gap-4">
            <aside className="flex flex-1 items-center gap-1">
              <h3 className="line-clamp-2 text-blue-400">{item.nome}</h3>

              {isCurrentItemSelected(item.cod_lista_preco) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="mr-1 size-5 max-sm:hidden"
                  onClick={handleNewProductFocus}
                >
                  <CirclePlus size={20} className="text-sky-500" />
                </Button>
              )}

              {!!item.qtde_produto && (
                <span className="text-sm text-green-500 max-sm:hidden">
                  ({item.qtde_produto} {!item.qtde_produto ? 'Item' : 'Itens'})
                </span>
              )}
            </aside>

            <aside className="flex items-center gap-4 sm:gap-2">
              {isCurrentItemSelected(item.cod_lista_preco) && (
                <>
                  <Info
                    className="text-sky-500 aria-disabled:cursor-not-allowed aria-disabled:text-gray-300"
                    size={16}
                    aria-disabled
                  />
                  <Settings
                    className="text-sky-500 aria-disabled:cursor-not-allowed aria-disabled:text-gray-300"
                    size={16}
                    aria-disabled
                  />
                </>
              )}
              <DeletePriceListDialog data={item} />
              <AccordionTrigger className="my-4 py-0 text-sky-500" />
            </aside>
          </div>

          <AccordionContent>
            <PriceListProductsTable id={item.cod_lista_preco} />
          </AccordionContent>
        </AccordionItem>
      ))}

      {priceListInfinite.isFetchingNextPage && <PriceListItemLoading />}

      <div ref={sentinelRef} className="h-1" />
    </Accordion>
  )
}
